import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { searchDocuments, rewriteQuery } from '../services/api';
import ClassificationBadge from '../components/ClassificationBadge';
import { MagnifyingGlassIcon, ClockIcon, DocumentIcon, SparklesIcon } from '@heroicons/react/24/outline';

export default function SearchDocuments() {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [topK, setTopK] = useState(10);
  const [useQueryRewriting, setUseQueryRewriting] = useState(false);
  const [classificationFilter, setClassificationFilter] = useState('');

  const searchMutation = useMutation({
    mutationFn: ({ query, topK, filters, rewriteQuery }) => searchDocuments(query, topK, filters, rewriteQuery),
    onSuccess: (data) => {
      setSearchResults(data);
    },
  });

  const rewriteMutation = useMutation({
    mutationFn: rewriteQuery,
    onSuccess: (data) => {
      setQuery(data.rewritten_query);
    },
  });

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    const filters = classificationFilter ? { classification: classificationFilter } : null;

    setSearchResults(null);
    searchMutation.mutate({ query, topK, filters, rewriteQuery: useQueryRewriting });
  };

  const handleQueryRewrite = () => {
    if (!query.trim()) return;
    rewriteMutation.mutate(query);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="section-header">
          <h2 className="!text-2xl">Search Documents</h2>
          <span className="control-number">SRCH-001</span>
        </div>
        <p className="text-sm text-[var(--text-muted)]">
          Search through classified documents using natural language queries
        </p>
      </div>

      {/* Search Form */}
      <div className="vault-card p-5 mb-8">
        <form onSubmit={handleSearch}>
          <div className="mb-4">
            <label htmlFor="search-query" className="block text-sm font-medium mb-2">
              Search Query
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                id="search-query"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="flex-1 vault-input"
                placeholder="Enter your search query..."
              />
              <button
                type="button"
                onClick={handleQueryRewrite}
                disabled={!query.trim() || rewriteMutation.isPending}
                className="vault-btn vault-btn-secondary disabled:opacity-50"
              >
                <SparklesIcon className="w-4 h-4" />
                {rewriteMutation.isPending ? 'Rewriting...' : 'Rewrite'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label htmlFor="top-k" className="block text-sm font-medium mb-1">
                Number of Results
              </label>
              <select
                id="top-k"
                value={topK}
                onChange={(e) => setTopK(parseInt(e.target.value))}
                className="block w-full"
              >
                <option value={5}>5 results</option>
                <option value={10}>10 results</option>
                <option value={20}>20 results</option>
                <option value={50}>50 results</option>
              </select>
            </div>

            <div>
              <label htmlFor="classification-filter" className="block text-sm font-medium mb-1">
                Classification Filter
              </label>
              <select
                id="classification-filter"
                value={classificationFilter}
                onChange={(e) => setClassificationFilter(e.target.value)}
                className="block w-full"
              >
                <option value="">All Classifications</option>
                <option value="UNCLASSIFIED">UNCLASSIFIED</option>
                <option value="CONFIDENTIAL">CONFIDENTIAL</option>
                <option value="SECRET">SECRET</option>
                <option value="TOP SECRET">TOP SECRET</option>
              </select>
            </div>

            <div className="flex items-end">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={useQueryRewriting}
                  onChange={(e) => setUseQueryRewriting(e.target.checked)}
                />
                <span className="ml-2 text-sm text-[var(--text-secondary)]">
                  Auto-rewrite query
                </span>
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={!query.trim() || searchMutation.isPending}
            className="vault-btn vault-btn-primary disabled:opacity-50"
          >
            {searchMutation.isPending ? (
              <>
                <ClockIcon className="w-4 h-4 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <MagnifyingGlassIcon className="w-4 h-4" />
                Search Documents
              </>
            )}
          </button>
        </form>
      </div>

      {/* Search Results */}
      {searchResults && (
        <div className="vault-card">
          <div className="px-5 py-4 border-b border-[var(--vault-border)]">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-primary)]">
                Search Results ({searchResults.total_hits} found)
              </h2>
              {searchResults.query_used !== query && (
                <div className="text-sm text-[var(--text-muted)]">
                  Query used: <span className="font-mono">{searchResults.query_used}</span>
                </div>
              )}
            </div>
          </div>

          <div className="divide-y divide-[var(--vault-border)]">
            {searchResults.results.length > 0 ? (
              searchResults.results.map((doc, index) => (
                <div key={doc.id || index} className="px-5 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <DocumentIcon className="w-5 h-5 text-[var(--text-muted)]" />
                        <h3 className="text-base font-medium text-[var(--text-primary)] truncate">
                          {doc.title || 'Untitled Document'}
                        </h3>
                        <ClassificationBadge
                          classification={doc.classification}
                          fullClassification={doc.full_classification}
                          caveats={doc.caveats}
                          releasability={doc.releasability}
                          confidence={doc.confidence}
                        />
                      </div>

                      <div className="text-sm text-[var(--text-muted)] mb-2">
                        Relevance Score: {(doc.score || 0).toFixed(3)}
                      </div>

                      {doc.content && (
                        <div className="text-sm text-[var(--text-secondary)]">
                          <p className="line-clamp-3">
                            {doc.content.substring(0, 300)}...
                          </p>
                        </div>
                      )}

                      {doc.metadata && (
                        <div className="mt-2 flex items-center space-x-4 text-xs text-[var(--text-muted)]">
                          {doc.metadata.word_count && (
                            <span>{doc.metadata.word_count} words</span>
                          )}
                          {doc.metadata.filename && (
                            <span>File: {doc.metadata.filename}</span>
                          )}
                          {doc.timestamp && (
                            <span>Added: {new Date(doc.timestamp).toLocaleDateString()}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-5 py-8 text-center">
                <DocumentIcon className="w-12 h-12 mx-auto text-[var(--text-muted)] mb-4" />
                <p className="text-[var(--text-muted)]">No documents found matching your search criteria.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Display */}
      {searchMutation.isError && (
        <div className="vault-card p-4 border-l-2 border-l-[var(--error)]">
          <h3 className="text-sm font-medium text-[var(--error)]">Search Failed</h3>
          <div className="mt-2 text-sm text-[var(--text-secondary)]">
            <p>{searchMutation.error?.response?.data?.detail || 'An error occurred during search.'}</p>
          </div>
        </div>
      )}
    </div>
  );
}
